import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { api } from '../../../../lib/api';

import './Module.scss'

/* ==========================================================================
   TIPAGENS DOS DADOS ANALÍTICOS E DIAGNÓSTICO
   ========================================================================== */

interface OverviewCounts {
  services: number;
  activeServices: number;
  projects: number;
  activeProjects: number;
  clients: number;
  partners: number;
  subscribers: number;
  admins: number;
  activeSessions: number;
  faqs: number;
  menuItems: number;
  settings: number;
}

interface OverviewData {
  counts: OverviewCounts;
  messagesByStatus: {
    new: number;
    read: number;
    closed: number;
    total: number;
  };
  recentMessages: Array<{
    id: string | number;
    name: string;
    email: string;
    subject: string;
    status: string;
    created_at: string;
  }>;
  driver?: 'd1' | 'mysql' | 'hybrid' | string;
  dbInfo?: {
    connected: boolean;
    d1Connected: boolean;
    mysqlConnected: boolean;
    databaseName?: string;
    host?: string;
    latencyMs?: number;
  };
}

/* ==========================================================================
   COMPONENTE: Visão Geral / Dashboard Analítico
   ========================================================================== */

export function AdminOverviewPage() {
  const [data, setData] = useState<OverviewData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [showDbModal, setShowDbModal] = useState<boolean>(false);

  // Fecha o modal ao pressionar a tecla ESC
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setShowDbModal(false);
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const loadStats = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.stats();

      const rawCounts = res.counts;
      const normalized: OverviewData = {
        counts: {
          services: Number(rawCounts?.services ?? 0),
          activeServices: Number(rawCounts?.activeServices ?? 0),
          projects: Number(rawCounts?.projects ?? 0),
          activeProjects: Number(rawCounts?.activeProjects ?? 0),
          clients: Number(rawCounts?.clients ?? 0),
          partners: Number(rawCounts?.partners ?? 0),
          subscribers: Number(rawCounts?.subscribers ?? 0),
          admins: Number(rawCounts?.admins ?? 0),
          activeSessions: Number(rawCounts?.activeSessions ?? 0),
          faqs: Number(rawCounts?.faqs ?? 0),
          menuItems: Number(rawCounts?.menuItems ?? 0),
          settings: Number(rawCounts?.settings ?? 0),
        },
        messagesByStatus: res.messagesByStatus ?? { new: 0, read: 0, closed: 0, total: 0 },
        recentMessages: res.recentMessages ?? [],
        driver: res.driver,
        dbInfo: res.dbInfo ? {
          connected: res.dbInfo.connected,
          d1Connected: res.dbInfo.driver === 'd1' && res.dbInfo.connected,
          mysqlConnected: res.dbInfo.driver === 'mysql' && res.dbInfo.connected,
          databaseName: res.dbInfo.driver,
          host: 'Cloudflare Pages Functions',
          latencyMs: res.dbInfo.latencyMs,
        } : undefined,
      };

      setData(normalized);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao conectar com a API analítica.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadStats();
  }, []);

  // Determina o texto amigável do status de conexão
  const isD1 = data?.dbInfo?.d1Connected ?? false;
  const isMySQL = data?.dbInfo?.mysqlConnected ?? false;
  const isHybrid = isD1 && isMySQL;

  const connectionLabel = isHybrid
    ? 'Híbrido (Cloudflare D1 + MySQL)'
    : isMySQL
    ? 'MySQL Database'
    : 'Cloudflare D1 (SQLite)';

  // Cálculos percentuais
  const totalMsgs = data?.messagesByStatus?.total || 1;
  const pctNew = Math.round(((data?.messagesByStatus?.new || 0) / totalMsgs) * 100);
  const pctRead = Math.round(((data?.messagesByStatus?.read || 0) / totalMsgs) * 100);
  const pctClosed = Math.round(((data?.messagesByStatus?.closed || 0) / totalMsgs) * 100);

  return (
    <div className="dash-overview-container">
      {/* Cabeçalho do Dashboard */}
      <div className="dash-view-header">
        <div>
          <h2>Visão Geral & Business Intelligence</h2>
          <div className="dash-db-status-bar">
            <span>Saúde real da API e do banco de dados:</span>
            {/* BOTÃO CLICÁVEL QUE ABRE O MODAL DE CONEXÃO */}
            <button
              type="button"
              className="dash-db-trigger-btn"
              onClick={() => setShowDbModal(true)}
              title="Clique para ver detalhes da conexão e configuração"
            >
              <span className="dash-dot green" />
              <strong>{connectionLabel}</strong>
              <span className="dash-db-online-badge">ONLINE</span>
              <Icon icon="lucide:info" width="14" height="14" />
            </button>
          </div>
        </div>

        <button type="button" className="btn btn-outline" onClick={loadStats} disabled={loading}>
          <Icon
            icon={loading ? 'lucide:loader-2' : 'lucide:refresh-cw'}
            width="16"
            height="16"
            className={loading ? 'dash-spin' : ''}
          />
          <span>{loading ? 'Atualizando…' : 'Atualizar Dados'}</span>
        </button>
      </div>

      {error && <p className="form-error" role="alert">{error}</p>}

      {/* 1. GRADE DE KPIS PRINCIPAIS */}
      <div className="dash-kpi-grid">
        <div className="dash-kpi-card">
          <div className="dash-kpi-icon-wrap" style={{ background: 'rgba(57, 117, 255, 0.12)', color: '#3975ff' }}>
            <Icon icon="lucide:layers" width="24" height="24" />
          </div>
          <div className="dash-kpi-info">
            <span className="dash-kpi-title">Serviços Ativos</span>
            <strong className="dash-kpi-val">
              {loading ? '…' : data?.counts?.activeServices ?? 0}
              <small className="muted"> / {data?.counts?.services ?? 0}</small>
            </strong>
          </div>
        </div>

        <div className="dash-kpi-card">
          <div className="dash-kpi-icon-wrap" style={{ background: 'rgba(0, 201, 255, 0.12)', color: '#00c9ff' }}>
            <Icon icon="lucide:briefcase" width="24" height="24" />
          </div>
          <div className="dash-kpi-info">
            <span className="dash-kpi-title">Projetos no Portfólio</span>
            <strong className="dash-kpi-val">
              {loading ? '…' : data?.counts?.activeProjects ?? 0}
              <small className="muted"> / {data?.counts?.projects ?? 0}</small>
            </strong>
          </div>
        </div>

        <div className="dash-kpi-card">
          <div className="dash-kpi-icon-wrap" style={{ background: 'rgba(16, 185, 129, 0.12)', color: '#10b981' }}>
            <Icon icon="lucide:users" width="24" height="24" />
          </div>
          <div className="dash-kpi-info">
            <span className="dash-kpi-title">Clientes & Parceiros</span>
            <strong className="dash-kpi-val">
              {loading ? '…' : ((data?.counts?.clients ?? 0) + (data?.counts?.partners ?? 0))}
            </strong>
          </div>
        </div>

        <div className="dash-kpi-card">
          <div className="dash-kpi-icon-wrap" style={{ background: 'rgba(245, 158, 11, 0.12)', color: '#f59e0b' }}>
            <Icon icon="lucide:mail" width="24" height="24" />
          </div>
          <div className="dash-kpi-info">
            <span className="dash-kpi-title">Mensagens Não Lidas</span>
            <strong className="dash-kpi-val" style={{ color: (data?.messagesByStatus?.new || 0) > 0 ? '#f59e0b' : 'inherit' }}>
              {loading ? '…' : data?.messagesByStatus?.new ?? 0}
            </strong>
          </div>
        </div>
      </div>

      {/* 2. GRID ANALÍTICO: TRÁFEGO EM BORDA & SAÚDE DO BANCO */}
      <div className="dash-bi-grid">
        <div className="dash-card dash-card-large">
          <div className="dash-card-header">
            <div>
              <h3>Saúde da aplicação</h3>
              <p>Verificação real da API e do banco de dados.</p>
            </div>
            <span className="dash-badge-live">Verificado</span>
          </div>
          <div className="dash-bi-placeholder">
            <div className="dash-health-list">
              <p><strong>API:</strong> {error ? 'com erro' : 'respondendo'}</p>
              <p><strong>Banco:</strong> {data?.dbInfo?.connected ? data.dbInfo.databaseName?.toUpperCase() : 'indisponível'}</p>
              <p><strong>Latência da consulta:</strong> {data?.dbInfo?.latencyMs ?? '—'} ms</p>
              <p><strong>Mensagens novas:</strong> {data?.messagesByStatus?.new ?? 0}</p>
            </div>
          </div>
        </div>

        <div className="dash-card">
          <div className="dash-card-header">
            <div>
              <h3>Saúde da Infraestrutura</h3>
              <p>Status real da conexão ativa</p>
            </div>
          </div>
          <div className="dash-status-list">
            <div className="dash-status-item">
              <div className="dash-status-label">
                <span className={`dash-dot ${data?.dbInfo?.connected ? 'green' : 'red'}`} />
                <span>Base Primária ({connectionLabel})</span>
              </div>
              <strong style={{ color: data?.dbInfo?.connected ? '#10b981' : '#ef4444' }}>{data?.dbInfo?.connected ? 'Conectada' : 'Indisponível'}</strong>
            </div>

            <div className="dash-status-item">
              <div className="dash-status-label">
                <span className="dash-dot green" />
                <span>Administradores Ativos</span>
              </div>
              <strong>{data?.counts?.admins ?? 0} contas</strong>
            </div>

            <div className="dash-status-item">
              <div className="dash-status-label">
                <span className="dash-dot blue" />
                <span>Sessões com sessão válida</span>
              </div>
              <strong style={{ color: '#0284c7' }}>{data?.counts?.activeSessions ?? 1} online</strong>
            </div>

            <div className="dash-status-item">
              <div className="dash-status-label">
                <span className="dash-dot green" />
                <span>Inscritos na newsletter</span>
              </div>
              <strong>{data?.counts?.subscribers ?? 0} leads</strong>
            </div>
          </div>
        </div>
      </div>

      {/* 3. GRID SECUNDÁRIO: FUNIL DE MENSAGENS & INVENTÁRIO DAS TABELAS */}
      <div className="dash-bi-grid" style={{ marginTop: '24px' }}>
        <div className="dash-card">
          <div className="dash-card-header">
            <div>
              <h3>Funil de Atendimento (contact_messages)</h3>
              <p>Distribuição das mensagens e contatos de clientes</p>
            </div>
          </div>

          <div className="dash-progress-bars">
            <div className="dash-progress-item">
              <div className="dash-progress-meta">
                <span><strong style={{ color: '#f59e0b' }}>Novas / Pendentes</strong> ({data?.messagesByStatus?.new ?? 0})</span>
                <span>{pctNew}%</span>
              </div>
              <div className="dash-progress-track">
                <div className="dash-progress-fill" style={{ width: `${pctNew}%`, background: '#f59e0b' }} />
              </div>
            </div>

            <div className="dash-progress-item">
              <div className="dash-progress-meta">
                <span><strong style={{ color: '#3975ff' }}>Lidas / Em Análise</strong> ({data?.messagesByStatus?.read ?? 0})</span>
                <span>{pctRead}%</span>
              </div>
              <div className="dash-progress-track">
                <div className="dash-progress-fill" style={{ width: `${pctRead}%`, background: '#3975ff' }} />
              </div>
            </div>

            <div className="dash-progress-item">
              <div className="dash-progress-meta">
                <span><strong style={{ color: '#10b981' }}>Fechadas / Concluídas</strong> ({data?.messagesByStatus?.closed ?? 0})</span>
                <span>{pctClosed}%</span>
              </div>
              <div className="dash-progress-track">
                <div className="dash-progress-fill" style={{ width: `${pctClosed}%`, background: '#10b981' }} />
              </div>
            </div>
          </div>

          <div className="dash-card-footer-stats">
            <span>Total de Mensagens: <strong>{data?.messagesByStatus?.total ?? 0}</strong></span>
          </div>
        </div>

        <div className="dash-card">
          <div className="dash-card-header">
            <div>
              <h3>Inventário de Conteúdo</h3>
              <p>Distribuição estrutural de registros no banco</p>
            </div>
            <span className="dash-badge-live">Conteúdo</span>
          </div>

          <div className="dash-entity-grid">
            <div className="dash-entity-item">
              <Icon icon="lucide:help-circle" width="18" height="18" />
              <span>Perguntas FAQ</span>
              <strong>{data?.counts?.faqs ?? 0}</strong>
            </div>
            <div className="dash-entity-item">
              <Icon icon="lucide:menu" width="18" height="18" />
              <span>Itens de Menu</span>
              <strong>{data?.counts?.menuItems ?? 0}</strong>
            </div>
            <div className="dash-entity-item">
              <Icon icon="lucide:sliders" width="18" height="18" />
              <span>Parâmetros SEO</span>
              <strong>{data?.counts?.settings ?? 0}</strong>
            </div>
            <div className="dash-entity-item">
              <Icon icon="lucide:handshake" width="18" height="18" />
              <span>Parceiros Cadastrados</span>
              <strong>{data?.counts?.partners ?? 0}</strong>
            </div>
          </div>
        </div>
      </div>

      {/* 4. TABELA DE MENSAGENS RECENTES */}
      <div className="dash-card" style={{ marginTop: '24px' }}>
        <div className="dash-card-header">
          <div>
            <h3>Últimas Mensagens Recebidas</h3>
            <p>Atendimentos recentes registrados no banco</p>
          </div>
          <Link to="/admin/messages" className="btn btn-small btn-outline">
            Ver todas as mensagens →
          </Link>
        </div>

        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Contato</th>
                <th>Assunto</th>
                <th>Status</th>
                <th>Data</th>
              </tr>
            </thead>
            <tbody>
              {(!data?.recentMessages || data.recentMessages.length === 0) ? (
                <tr>
                  <td colSpan={4} style={{ textAlign: 'center', padding: '24px' }}>
                    Nenhuma mensagem registrada.
                  </td>
                </tr>
              ) : (
                data.recentMessages.map((msg) => (
                  <tr key={msg.id}>
                    <td>
                      <strong>{msg.name}</strong>
                      <small>{msg.email}</small>
                    </td>
                    <td>{msg.subject}</td>
                    <td>
                      <span className={`status-pill status-${msg.status}`}>
                        {msg.status === 'new' ? 'Nova' : msg.status === 'read' ? 'Lida' : 'Fechada'}
                      </span>
                    </td>
                    <td>
                      <small>{msg.created_at ? new Date(msg.created_at).toLocaleString('pt-BR') : 'Recente'}</small>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* =========================================================
          MODAL DE CONFIGURAÇÃO E DIAGNÓSTICO DO BANCO DE DADOS
          ========================================================= */}
      {showDbModal && (
        <div className="dash-modal-overlay" onClick={() => setShowDbModal(false)}>
          <div className="dash-modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="dash-modal-header">
              <div className="dash-modal-title-wrap">
                <Icon icon="lucide:database" width="22" height="22" className="dash-modal-icon" />
                <h3>Diagnóstico & Configuração do Banco de Dados</h3>
              </div>
              <button
                type="button"
                className="dash-modal-close-btn"
                onClick={() => setShowDbModal(false)}
                aria-label="Fechar modal"
              >
                <Icon icon="lucide:x" width="20" height="20" />
              </button>
            </div>

            <div className="dash-modal-body">
              {/* Status dos Motores */}
              <div className="dash-modal-status-cards">
                <div className={`dash-db-card ${isD1 ? 'connected' : 'disconnected'}`}>
                  <div className="dash-db-card-head">
                    <strong>Cloudflare D1 (SQLite)</strong>
                    <span className={`dash-badge-status ${isD1 ? 'online' : 'offline'}`}>
                      {isD1 ? 'ONLINE' : 'OFFLINE'}
                    </span>
                  </div>
                  <p>Borda distribuída de alta velocidade com replicação global.</p>
                </div>

                <div className={`dash-db-card ${isMySQL ? 'connected' : 'disconnected'}`}>
                  <div className="dash-db-card-head">
                    <strong>MySQL / MariaDB</strong>
                    <span className={`dash-badge-status ${isMySQL ? 'online' : 'offline'}`}>
                      {isMySQL ? 'ONLINE' : 'OFFLINE'}
                    </span>
                  </div>
                  <p>Banco relacional com suporte a pooling e transações ACID.</p>
                </div>
              </div>

              {/* Informações Técnicas e Parâmetros */}
              <div className="dash-modal-section">
                <h4>Parâmetros da Conexão Ativa</h4>
                <div className="dash-modal-info-grid">
                  <div className="dash-modal-info-row">
                    <span className="muted">Modo de Operação:</span>
                    <strong>{connectionLabel}</strong>
                  </div>
                  <div className="dash-modal-info-row">
                    <span className="muted">Driver ativo:</span>
                    <code>{data?.dbInfo?.databaseName || '—'}</code>
                  </div>
                  <div className="dash-modal-info-row">
                    <span className="muted">Ambiente:</span>
                    <span>{data?.dbInfo?.host || 'Cloudflare Pages Functions'}</span>
                  </div>
                  <div className="dash-modal-info-row">
                    <span className="muted">Latência de Consulta:</span>
                    <strong style={{ color: '#10b981' }}>{data?.dbInfo?.latencyMs ?? '—'} ms</strong>
                  </div>
                  <div className="dash-modal-info-row">
                    <span className="muted">Driver configurado:</span>
                    <span>{data?.driver || '—'}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="dash-modal-footer">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setShowDbModal(false)}
              >
                Entendido / Fechar
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}